import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  Fuel, Wifi, WifiOff, RefreshCw, Clock, Activity,
  AlertTriangle, CheckCircle2, CircleDot, Gauge
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  idle:       { label: 'Idle',       color: 'bg-slate-500',   text: 'text-slate-400',   dot: 'bg-slate-400' },
  calling:    { label: 'Calling',    color: 'bg-blue-500',    text: 'text-blue-400',    dot: 'bg-blue-400' },
  authorized: { label: 'Authorized', color: 'bg-cyan-500',    text: 'text-cyan-400',    dot: 'bg-cyan-400' },
  fueling:    { label: 'Pumping',    color: 'bg-emerald-500', text: 'text-emerald-400', dot: 'bg-emerald-400' },
  prepay:     { label: 'Prepay',     color: 'bg-purple-500',  text: 'text-purple-400',  dot: 'bg-purple-400' },
  postpay:    { label: 'Complete',   color: 'bg-amber-500',   text: 'text-amber-400',   dot: 'bg-amber-400' },
  stopped:    { label: 'Stopped',    color: 'bg-red-500',     text: 'text-red-400',     dot: 'bg-red-400' },
  offline:    { label: 'Offline',    color: 'bg-gray-600',    text: 'text-gray-500',    dot: 'bg-gray-500' },
  error:      { label: 'Error',      color: 'bg-red-600',     text: 'text-red-500',     dot: 'bg-red-500' },
  unknown:    { label: 'Unknown',    color: 'bg-gray-500',    text: 'text-gray-400',    dot: 'bg-gray-400' },
};

export default function LivePumpMonitor() {
  const { token } = useAuth();
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('checking');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLiveData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fuel-station/live-data`, { headers });
      setLiveData(res.data);
      setLastUpdate(new Date());
      
      if (res.data.status === 'no_data') {
        setConnectionStatus('waiting');
      } else if (res.data.received_at) {
        const receivedAt = new Date(res.data.received_at);
        const ageSeconds = (Date.now() - receivedAt.getTime()) / 1000;
        setConnectionStatus(ageSeconds < 30 ? 'live' : ageSeconds < 120 ? 'stale' : 'offline');
      }
    } catch (err) {
      console.error('Failed to fetch live data:', err);
      setConnectionStatus('error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLiveData();
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchLiveData, 5000);
    }
    return () => clearInterval(interval);
  }, [fetchLiveData, autoRefresh]);

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.unknown;

  const ConnectionBadge = () => {
    const configs = {
      live:     { icon: Wifi,    text: 'Live',              bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', textColor: 'text-emerald-400' },
      stale:    { icon: Activity, text: 'Stale Data',       bg: 'bg-amber-500/20',   border: 'border-amber-500/40',   textColor: 'text-amber-400' },
      waiting:  { icon: Clock,   text: 'Waiting for Agent', bg: 'bg-blue-500/20',    border: 'border-blue-500/40',    textColor: 'text-blue-400' },
      offline:  { icon: WifiOff, text: 'Offline',           bg: 'bg-red-500/20',     border: 'border-red-500/40',     textColor: 'text-red-400' },
      error:    { icon: AlertTriangle, text: 'Error',       bg: 'bg-red-500/20',     border: 'border-red-500/40',     textColor: 'text-red-400' },
      checking: { icon: RefreshCw, text: 'Connecting...',   bg: 'bg-slate-500/20',   border: 'border-slate-500/40',   textColor: 'text-slate-400' },
    };
    const cfg = configs[connectionStatus] || configs.checking;
    const Icon = cfg.icon;
    
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${cfg.bg} border ${cfg.border}`} data-testid="connection-badge">
        <Icon className={`w-4 h-4 ${cfg.textColor} ${connectionStatus === 'checking' ? 'animate-spin' : ''}`} />
        <span className={`text-sm font-medium ${cfg.textColor}`}>{cfg.text}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pump-monitor-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const pumps = liveData?.pumps || [];
  const hasPumpData = pumps.length > 0 && pumps[0]?.pump_number;
  
  const activePumps = hasPumpData ? pumps.filter(p => p.status === 'fueling').length : 0;
  const idlePumps = hasPumpData ? pumps.filter(p => p.status === 'idle').length : 0;
  const callingPumps = hasPumpData ? pumps.filter(p => p.status === 'calling').length : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="live-pump-monitor-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100" data-testid="page-title">Live Pump Monitor</h1>
          <p className="text-sm text-slate-400 mt-1">
            Verifone Commander @ 192.168.31.11 | Site 1499
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ConnectionBadge />
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              autoRefresh 
                ? 'bg-emerald-600/20 text-emerald-400 border border-emerald-600/30' 
                : 'bg-slate-700 text-slate-400 border border-slate-600'
            }`}
            data-testid="auto-refresh-toggle"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Paused'}
          </button>
        </div>
      </div>

      {/* Connection Status Banner */}
      {connectionStatus === 'waiting' && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4" data-testid="agent-setup-banner">
          <h3 className="text-blue-400 font-semibold flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Station Agent Not Connected
          </h3>
          <p className="text-sm text-blue-300/70 mt-1">
            Run the FlowFeed Station Agent on your station PC to start receiving live pump data.
          </p>
          <div className="mt-3 bg-slate-900/60 rounded-lg p-3 font-mono text-xs text-slate-300">
            <p className="text-slate-500"># On the station Windows PC:</p>
            <p>pip install requests</p>
            <p>python flowfeed_station_agent.py</p>
            <p className="text-slate-500"># Select option 1 (Discovery) first, then 2 (Monitor)</p>
          </div>
          <a
            href={`${API_URL}/api/station-agent/download`}
            download="flowfeed_station_agent.py"
            className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="download-agent-btn"
          >
            Download Station Agent
          </a>
        </div>
      )}

      {/* Stats Summary */}
      {hasPumpData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pump-stats">
          <div className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Total Pumps</p>
            <p className="text-2xl font-bold text-slate-100 mt-1">{pumps.length}</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
            <p className="text-xs text-emerald-400 uppercase tracking-wider">Active</p>
            <p className="text-2xl font-bold text-emerald-400 mt-1">{activePumps}</p>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-4">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Idle</p>
            <p className="text-2xl font-bold text-slate-300 mt-1">{idlePumps}</p>
          </div>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
            <p className="text-xs text-blue-400 uppercase tracking-wider">Calling</p>
            <p className="text-2xl font-bold text-blue-400 mt-1">{callingPumps}</p>
          </div>
        </div>
      )}

      {/* Pump Grid */}
      {hasPumpData ? (
        <div>
          <h2 className="text-lg font-semibold text-slate-200 mb-3">Pump Status</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="pump-grid">
            {pumps.map((pump) => {
              const cfg = getStatusConfig(pump.status);
              return (
                <div
                  key={pump.pump_number}
                  className="bg-slate-800/50 border border-slate-700/40 rounded-xl p-4 hover:border-slate-600 transition-colors"
                  data-testid={`pump-card-${pump.pump_number}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Fuel className="w-5 h-5 text-slate-400" />
                      <span className="font-bold text-slate-200 text-lg">Pump {pump.pump_number}</span>
                    </div>
                    <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${pump.status === 'fueling' ? 'animate-pulse' : ''}`} />
                  </div>
                  
                  <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-semibold ${cfg.color}/20 ${cfg.text}`}>
                    <CircleDot className="w-3 h-3" />
                    {cfg.label}
                  </div>

                  {pump.status === 'fueling' && (
                    <div className="mt-3 space-y-1">
                      <div className="flex justify-between text-xs text-slate-400">
                        <span>{pump.current_grade || 'Regular'}</span>
                        <span>${pump.current_price_per_gallon?.toFixed(2) || '0.00'}/gal</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-300">{pump.current_gallons?.toFixed(2) || '0.00'} gal</span>
                        <span className="text-emerald-400 font-bold">${pump.current_amount?.toFixed(2) || '0.00'}</span>
                      </div>
                      {pump.is_prepay && (
                        <div className="text-xs text-purple-400">
                          Prepay: ${pump.prepay_remaining?.toFixed(2)} remaining
                        </div>
                      )}
                    </div>
                  )}

                  {pump.last_transaction && pump.status === 'idle' && (
                    <div className="mt-3 text-xs text-slate-500">
                      Last: {pump.last_transaction.gallons?.toFixed(1)} gal = ${pump.last_transaction.amount?.toFixed(2)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-8 text-center" data-testid="no-pump-data">
          <Gauge className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-300">No Live Pump Data</h3>
          <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
            {connectionStatus === 'waiting' 
              ? 'Start the Station Agent on your station PC to begin receiving live pump data.'
              : 'Pump data will appear here once the Station Agent connects to the Commander.'}
          </p>
        </div>
      )}

      {/* Raw Data Preview (for debugging) */}
      {liveData && liveData.received_at && (
        <div className="mt-6">
          <details className="bg-slate-800/30 border border-slate-700/30 rounded-xl">
            <summary className="px-4 py-3 text-sm text-slate-400 cursor-pointer hover:text-slate-300">
              Raw Data (Debug) - Last received: {new Date(liveData.received_at).toLocaleString()}
            </summary>
            <pre className="px-4 pb-4 text-xs text-slate-500 overflow-auto max-h-60" data-testid="raw-data-debug">
              {JSON.stringify(liveData, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Last Update Footer */}
      {lastUpdate && (
        <div className="text-center text-xs text-slate-600" data-testid="last-update-time">
          Dashboard refreshed: {lastUpdate.toLocaleTimeString()}
          {autoRefresh && ' (auto-refreshing every 5s)'}
        </div>
      )}
    </div>
  );
}
